<template>
  <div class="relative mb-sm" v-click-outside="closeOptions">
    <div @click="dropdownToggled">
      <input
          class="flex items-center p-5 py-base shadow-none outline-none justify-between text-black cursor-pointer bg-whiteOverride rounded-6 border-1 border-solid border-border w-full sm:min-w-floatingLabelInputMinWidth leading-6 focus:border-primary hover:border-placeholderGray"
          :class="{ 'tw-border-error': hasError }"
          :value="value"
          data-test="dropdown"
          :placeholder="inputDisplay"
          @keydown.up.prevent="handleUpKey"
          @keydown.esc.prevent="showOptions = false"
          @keydown.down.prevent="handleDownKey"
          @keydown.enter.prevent="handleEnter"
          readonly
      />
      <div class="absolute right-base top-twenty">
        <ChevronDownIcon
          class="cursor-pointer chevron text-border"
          :style="chevronStyle"
        />
      </div>
      <div data-test="label" class="cursor-pointer text-10 absolute top-2 left-5 bg-transparent inline-block rounded-6 text-primary" v-if="showLabel">
        <span>{{ placeholder }}</span>
        <span v-if="fieldIsRequired"> *</span>
      </div>
    </div>
    <HeightTransition>
      <ul
        v-if="showOptions"
        ref="list"
        class="absolute z-10 w-full border-1 border-border border-solid list-none bg-whiteOverride p-none rounded-6 mt-0"
        data-test="list"
      >
        <li
            v-for="(option, index) in options"
            :key="index"
            class="hover:bg-hoverGray p-xs cursor-pointer w-full first:mt-base last:mb-base"
            :class="{'bg-primary text-whiteOverride': value === option}"
            @click="selectOption(option)"
            :data-test="`option-${option}`"
        >
          <span class="ml-base">{{ option }}</span>
        </li>
      </ul>
    </HeightTransition>
  </div>
</template>

<script>
import { ChevronDownIcon } from '@/components/icons/fontIcons';
import HeightTransition from '@/transitions/HeightTransition.vue';

export default {
  name: 'FloatingLabelDropdown',
  props: {
    options: { // expects an array of strings
      type: Array,
      required: true,
    },
    fieldIsRequired: {
      type: Boolean,
      default: false,
    },
    value: {},
    placeholder: {
      type: String,
      required: true,
    },
    hasError: {
      type: Boolean,
    },
  },
  components: {
    HeightTransition,
    ChevronDownIcon,
  },
  data() {
    return {
      showOptions: false,
    };
  },
  computed: {
    placeholderTextRequiredOrNot() {
      return this.fieldIsRequired ? `${ this.placeholder } *` : this.placeholder;
    },
    inputDisplay() {
      return this.value ? this.value : this.placeholderOrBlank;
    },
    placeholderOrBlank() {
      return this.showOptions ? '' : this.placeholderTextRequiredOrNot;
    },
    showLabel() {
      return !!(this.showOptions || this.value);
    },
    chevronStyle() {
      const rotate = this.showOptions ? 180 : 360;

      return {
        transform: `rotate(${rotate}deg)`,
      };
    },
  },
  methods: {
    closeOptions() {
      this.showOptions = false;
    },
    dropdownToggled() {
      this.showOptions = !this.showOptions;

      // ensure list has updated before scrolling
      window.requestAnimationFrame(() => {
        if (this.showOptions && this.$refs.list) {
          this.$refs.list.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    },
    handleEnter() {
      this.showOptions = !this.showOptions;
    },
    handleUpKey() {
      const position = this.options.indexOf(this.value);
      if (!this.showOptions) {
        this.showOptions = true;
        return;
      }
      if (position === -1) {
        this.$emit('input', this.options[0]);
        return;
      }

      if (position > 0 && position < this.options.length) {
        this.$emit('input', this.options[position - 1]);
      }
    },
    handleDownKey() {
      if (!this.showOptions) {
        this.showOptions = true;
        return;
      }
      const position = this.options.indexOf(this.value);
      if (position === -1) {
        this.$emit('input', this.options[0]);
        return;
      }

      if (position >= 0 && position < this.options.length - 1) {
        this.$emit('input', this.options[position + 1]);
      }
    },
    selectOption(option) {
      this.showOptions = false;
      this.$emit('input', option);
    },
  },
};
</script>
